import { C } from '@parsina/framework/tokens';
import { LTR_TEXT } from '../../i18n';
import MobileCard, { MobileCardMeta } from '../MobileCard';
import MobileMoneyRow from '../MobileMoneyRow';

/**
 * One insurance claim, as a card. Read-only, by scope decision.
 *
 * The claims ledger is the archetypal §2.4-rule-3 capability: sending a batch,
 * recording what an insurer actually paid and deciding what happens to the
 * کسورات are multi-field definition tasks done sitting down, on a wide screen,
 * against a statement. So the phone shows the ledger and says, in words, where
 * the work is done — it does not offer a cramped, half-safe version of it.
 *
 * What it must NOT do is hide the numbers. What an insurer still owes is money
 * the clinic is counting on, so the three figures — claimed, paid, outstanding
 * — are the card's body, as full-width rows rather than a KPI grid: «۱۲٬۴۵۰٬۰۰۰»
 * in a third of 320px either wraps mid-number or takes the page sideways.
 *
 * The invoice number is an LTR island. It is a digit string, not a quantity, so
 * it is neither grouped nor allowed to reorder inside Persian text.
 */
export default function MobileClaimCard({
  patientName,
  insurerName,
  invoiceNumber,
  invoiceNumberLabel,
  /** `{ label, bg, text }` — the claim's status, from the shared vocabulary. */
  statusBadge,
  /** `[{ key, label, value, tone, strong }]` — claimed / paid / outstanding. */
  moneyRows = [],
  /** Shown when a shortfall still has no disposition recorded. */
  note,
  'data-testid': testId,
}) {
  return (
    <MobileCard data-testid={testId} data-state="content">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.5rem' }}>
        <div style={{ minWidth: 0 }}>
          <div
            dir="auto"
            style={{
              fontSize: '0.8125rem', fontWeight: 700, color: C.textPrimary,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}
          >
            {patientName}
          </div>
          <MobileCardMeta parts={[insurerName]} />
        </div>

        {statusBadge ? (
          <span
            data-testid={testId ? `${testId}-status` : undefined}
            style={{
              flexShrink: 0,
              padding: '0.125rem 0.5rem', borderRadius: '999px',
              fontSize: '0.625rem', fontWeight: 700,
              background: statusBadge.bg, color: statusBadge.text, whiteSpace: 'nowrap',
            }}
          >
            {statusBadge.label}
          </span>
        ) : null}
      </div>

      {invoiceNumber ? (
        <div style={{ marginTop: '0.375rem', fontSize: '0.6875rem', color: C.textMuted }}>
          {invoiceNumberLabel}{' '}
          <span {...LTR_TEXT} style={{ fontVariantNumeric: 'tabular-nums' }}>{invoiceNumber}</span>
        </div>
      ) : null}

      <div style={{ marginTop: '0.5rem', display: 'grid', gap: '0.125rem' }}>
        {moneyRows.map((r) => (
          <MobileMoneyRow
            key={r.key}
            label={r.label}
            value={r.value}
            unit={r.unit}
            tone={r.tone}
            strong={r.strong}
            data-testid={testId ? `${testId}-${r.key}` : undefined}
          />
        ))}
      </div>

      {note ? (
        <p style={{ margin: '0.375rem 0 0', fontSize: '0.6875rem', color: C.amber, lineHeight: 1.6 }}>
          {note}
        </p>
      ) : null}
    </MobileCard>
  );
}
